import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Droplet, TrendingDown, Zap, Shield, Users, Ban, CheckCircle, Clock, Search } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import Breadcrumb from './Breadcrumb';

const ILKHunter = () => {
  const [detections, setDetections] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDetection, setSelectedDetection] = useState(null);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      
      const [detectionsRes, statsRes] = await Promise.all([
        api.ilk.getAll(params),
        api.ilk.getStats()
      ]);
      
      setDetections(detectionsRes.data.data || []);
      setStats(statsRes.data.data);
    } catch (error) {
      console.error('Failed to fetch ILK data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleIsolate = async (ilkId) => {
    try {
      const valveId = `valve_${ilkId}_isolation`;
      await api.ilk.isolate(ilkId, valveId);
      alert('ILK line isolated successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to isolate ILK:', error);
      alert('Failed to isolate ILK line');
    }
  };

  const handleDeployTeam = async (ilkId) => {
    try {
      const teamId = `team_field_${Date.now()}`;
      await api.ilk.deployTeam(ilkId, teamId);
      alert('Field team deployed successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to deploy team:', error);
      alert('Failed to deploy field team');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'text-red-400 bg-red-500/20 border-red-500/50';
      case 'High': return 'text-orange-400 bg-orange-500/20 border-orange-500/50';
      case 'Medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
      case 'Low': return 'text-green-400 bg-green-500/20 border-green-500/50';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/50';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Detected': return 'text-red-400';
      case 'Isolated': return 'text-orange-400';
      case 'UnderInvestigation': return 'text-yellow-400';
      case 'Resolved': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Leakage': return <Droplet className="text-cyan-400" size={18} />;
      case 'IllegalTap': return <Ban className="text-red-400" size={18} />;
      case 'Tanker': return <TrendingDown className="text-orange-400" size={18} />;
      default: return <Search className="text-gray-400" size={18} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <Zap className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
            </div>
          </div>
          <p className="text-cyan-400 font-semibold tracking-wide">INITIALIZING ILK HUNTER...</p>
          <p className="text-gray-500 text-sm mt-2">Loading detection data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'ILK Hunter' }]} onNavigate={() => {}} />
      
      {/* Header */}
      <div className="relative rounded-xl overflow-hidden border border-red-500/30 bg-gradient-to-br from-[#12172e] via-[#0f1629] to-[#12172e] p-6 shadow-2xl">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(0deg, rgba(255, 0, 85, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 0, 85, 0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 tracking-wide">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400">
                  ILK HUNTER
                </span>
              </h1>
              <p className="text-gray-400 text-sm md:text-base">Illegal/Leakage/Kabootarkhana Detection & Control System</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-red-400 font-semibold text-sm">LIVE MONITORING</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-[#12172e] border-red-500/30 text-white hover:border-red-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">TOTAL DETECTIONS</p>
                  <p className="text-4xl font-bold text-red-400 mt-2">{stats.total}</p>
                </div>
                <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30">
                  <AlertTriangle className="text-red-400" size={32} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-orange-500/30 text-white hover:border-orange-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">ACTIVE LOSS</p>
                  <p className="text-4xl font-bold text-orange-400 mt-2">{stats.total_loss_kl_hr.toFixed(1)}</p>
                  <p className="text-xs text-gray-500 mt-1">KL/hour</p>
                </div>
                <div className="bg-orange-500/10 p-4 rounded-lg border border-orange-500/30">
                  <TrendingDown className="text-orange-400" size={32} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-yellow-500/30 text-white hover:border-yellow-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">REVENUE POTENTIAL</p>
                  <p className="text-4xl font-bold text-yellow-400 mt-2">₹{(stats.total_revenue_potential / 100000).toFixed(1)}</p>
                  <p className="text-xs text-gray-500 mt-1">Lakhs/month</p>
                </div>
                <div className="bg-yellow-500/10 p-4 rounded-lg border border-yellow-500/30">
                  <Zap className="text-yellow-400" size={32} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#12172e] border-green-500/30 text-white hover:border-green-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400 font-medium tracking-wide">RESOLVED</p>
                  <p className="text-4xl font-bold text-green-400 mt-2">{stats.resolved}</p>
                </div>
                <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30">
                  <CheckCircle className="text-green-400" size={32} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#12172e] border-cyan-500/30 text-white hover:border-cyan-500/50 transition-all">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium tracking-wide">FILTER:</span>
            {['all', 'Detected', 'Isolated', 'UnderInvestigation', 'Resolved'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === status
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                    : 'bg-slate-800/50 text-gray-400 border border-slate-700/50 hover:border-cyan-500/30'
                }`}
              >
                {status === 'all' ? 'ALL' : status.replace(/([A-Z])/g, ' $1').toUpperCase()}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detection List */}
      <div className="grid grid-cols-1 gap-4">
        {detections.length === 0 ? (
          <Card className="bg-[#12172e] border-green-500/30 text-white">
            <CardContent className="p-12 text-center">
              <Shield size={64} className="mx-auto mb-4 text-green-400 opacity-50" />
              <p className="text-xl font-semibold text-green-400">NO ACTIVE ILK DETECTIONS</p>
              <p className="text-sm text-gray-500 mt-2">System monitoring all entry and exit points</p>
            </CardContent>
          </Card>
        ) : (
          detections.map((detection) => (
            <Card key={detection.id} className="bg-[#12172e] border-red-500/30 text-white hover:border-red-500/50 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getTypeIcon(detection.type)}
                      <h3 className="text-xl font-bold text-white tracking-wide">{detection.type.toUpperCase()}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getSeverityColor(detection.severity)}`}>
                        {detection.severity}
                      </span>
                      <span className={`text-sm font-medium ${getStatusColor(detection.status)}`}>
                        {detection.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <p className="text-gray-400 text-xs mb-1 tracking-wide">Entry Flow (QS1)</p>
                        <p className="text-cyan-400 font-semibold">{detection.qs1_flow.toFixed(2)} KL/min</p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <p className="text-gray-400 text-xs mb-1 tracking-wide">Exit Flow (QS2)</p>
                        <p className="text-cyan-400 font-semibold">{detection.qs2_flow.toFixed(2)} KL/min</p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <p className="text-gray-400 text-xs mb-1 tracking-wide">Loss Rate</p>
                        <p className="text-red-400 font-semibold">{detection.loss_kl_hr.toFixed(2)} KL/hr</p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <p className="text-gray-400 text-xs mb-1 tracking-wide">Revenue Potential</p>
                        <p className="text-yellow-400 font-semibold">₹{(detection.revenue_potential / 1000).toFixed(0)}K/mo</p>
                      </div>
                      <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                        <p className="text-gray-400 text-xs mb-1 tracking-wide">Detected</p>
                        <p className="text-white text-xs">{new Date(detection.detection_timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    {detection.status === 'Detected' && (
                      <>
                        <Button
                          onClick={() => handleIsolate(detection.id)}
                          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 border-0 text-white font-semibold flex items-center gap-2"
                        >
                          <Ban size={16} />
                          Isolate Line
                        </Button>
                        <Button
                          onClick={() => handleDeployTeam(detection.id)}
                          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 border-0 text-white font-semibold flex items-center gap-2"
                        >
                          <Users size={16} />
                          Deploy Team
                        </Button>
                      </>
                    )}
                    {detection.status === 'Isolated' && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/20 border border-orange-500/50 rounded-lg">
                        <Clock size={16} className="text-orange-400" />
                        <span className="text-sm text-orange-400 font-semibold">ISOLATED</span>
                      </div>
                    )}
                    {detection.status === 'Resolved' && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/50 rounded-lg">
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-sm text-green-400 font-semibold">RESOLVED</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ILKHunter;
